import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { requestThrottle, delay } from '../utils/requestThrottle';

// API Configuration - Prefer Vercel proxy in production to avoid cert issues; backend will use real client IP
const inferBaseUrl = () => {
  // Runtime check: if we're in the browser, check hostname
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    
    // Only use localhost if actually on localhost
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname.endsWith('.local')) {
      // Allow override via env in local dev
      return process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
    }
    
    // For production (Vercel or any other domain), use proxy path
    // The backend now reads real client IP from X-Forwarded-For
    return '/api';
  }
  
  // For server-side rendering or build time, use production API or env override
  return process.env.REACT_APP_API_URL || 'https://api.gotriveni.com/api';
};

const API_BASE_URL = inferBaseUrl();

// Log for debugging - always log in production to debug
console.log('[API Config] Base URL:', API_BASE_URL);
console.log('[API Config] Hostname:', typeof window !== 'undefined' ? window.location.hostname : 'N/A');
console.log('[API Config] NODE_ENV:', process.env.NODE_ENV);
console.log('[API Config] REACT_APP_API_URL:', process.env.REACT_APP_API_URL || 'not set');

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true, // Include credentials for CORS
});

// Request interceptor to add auth token and throttle requests
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Light throttling for GET requests to prevent overwhelming the server
    // Reduced delay since we're calling directly and backend has rate limiting
    if (config.method === 'get' && config.url) {
      const fullUrl = `${config.baseURL || ''}${config.url}${config.params ? '?' + new URLSearchParams(config.params).toString() : ''}`;
      if (!requestThrottle.shouldProceed(fullUrl)) {
        // Reduced delay from 500ms to 100ms for better responsiveness
        await delay(100);
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error: AxiosError) => {
    const statusCode = error.response?.status;

    // Handle rate limiting (429 Too Many Requests)
    if (statusCode === 429) {
      const retryAfter = error.response?.headers['retry-after'];
      const waitSeconds = retryAfter ? parseInt(retryAfter, 10) || 5 : 5;
      
      // Wait before retrying
      await delay(waitSeconds * 1000);
      
      // Clear throttle for this URL to allow retry
      const requestPath = error.config?.url || '';
      requestThrottle.clear(requestPath);
      
      // Retry the request once
      if (error.config) {
        try {
          return await api.request(error.config);
        } catch (retryError) {
          // If retry fails, show user-friendly message
          console.error('Request failed after retry:', retryError);
        }
      }
    }

    if (statusCode === 401) {
      // Token expired or invalid
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      window.location.href = '/login';
    }
    
    return Promise.reject(error);
  }
);

export default api;
